import { isEmpty, isNotEmpty, splitTextIntoNonEmptyLines } from '@/shared/util';

import { LIST_PATTERN } from '../editor.constant';
import { isHtmlContent } from './content.util';
import { extractTextFromHtml } from './extract.util';
import {
  buildBulletPointsHtml,
  cleanHtmlWithLists,
  convertTextBeforeListToBulletPoints,
} from './list';
import { convertEscapeSequencesToNewlines, escapeHtml, isLevelHeader } from './text.util';

function isBlockHtmlLine(line: string): boolean {
  return /^<(p|div|h[1-6])(\s|>)/i.test(line.trim());
}

function splitHtmlByNewlinesPreservingFormatting(html: string): string[] {
  const normalizedHtml = convertEscapeSequencesToNewlines(html);

  if (!normalizedHtml.includes('\n')) {
    return [html];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${normalizedHtml}</div>`, 'text/html');
  const wrapper = doc.body.firstElementChild;

  if (!wrapper) {
    return [html];
  }

  const lines: string[] = [];
  let currentLineFragments: string[] = [];

  const flushCurrentLine = () => {
    const lineHtml = currentLineFragments.join('').trim();

    if (lineHtml) {
      lines.push(lineHtml);
    }

    currentLineFragments = [];
  };

  Array.from(wrapper.childNodes).forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const parts = text.split('\n');

      for (let i = 0; i < parts.length; i++) {
        if (parts[i]) {
          currentLineFragments.push(parts[i]);
        }

        if (i < parts.length - 1) {
          flushCurrentLine();
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      currentLineFragments.push((node as Element).outerHTML);
    }
  });

  flushCurrentLine();

  return isNotEmpty(lines) ? lines : [html];
}

function hasMultipleBlockElements(html: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blockElements = doc.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, li');

  return blockElements.length > 1;
}

function hasNewlinesBetweenElements(html: string): boolean {
  const normalizedHtml = convertEscapeSequencesToNewlines(html);

  if (!normalizedHtml.includes('\n')) {
    return false;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${normalizedHtml}</div>`, 'text/html');
  const wrapper = doc.body.firstElementChild;

  if (!wrapper) {
    return false;
  }

  const childNodes = Array.from(wrapper.childNodes);

  if (childNodes.length <= 1) {
    return false;
  }

  for (const node of childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';

      if (text.includes('\n')) {
        return true;
      }
    }
  }

  return false;
}

function hasFormattingInBlockElements(html: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blockTags = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
  const topLevelBlocks = Array.from(doc.body.children).filter((el) => blockTags.has(el.tagName));

  return topLevelBlocks.some((el) => el.querySelector('strong, em, u, b, i'));
}

function extractInnerHtmlFromBlockElements(html: string): string[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blockTags = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
  const topLevelBlocks = Array.from(doc.body.children).filter((el) => blockTags.has(el.tagName));

  return topLevelBlocks
    .map((el) => el.innerHTML.trim())
    .filter((content) => content && content !== '<br>');
}

function returnHtmlUnlessEmpty(html: string): string {
  const extractedText = extractTextFromHtml(html);

  return isNotEmpty(extractedText) ? html : '';
}

// Build HTML line-by-line, opening/closing <ol> around non-header lines so
// section headers (e.g. "Postmenopause:") render as paragraphs, not bullets.
function buildBulletListFromHtmlLines(nonEmptyLines: string[]): string {
  const parts: string[] = [];
  let isListOpen = false;

  nonEmptyLines.forEach((line) => {
    const lineText = extractTextFromHtml(line).trim();

    if (isLevelHeader(lineText)) {
      if (isListOpen) {
        parts.push('</ol>');
        isListOpen = false;
      }

      parts.push(isBlockHtmlLine(line) ? line : `<p>${line}</p>`);

      return;
    }

    if (!isListOpen) {
      parts.push('<ol>');
      isListOpen = true;
    }

    parts.push(
      `<li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>${line}</li>`,
    );
  });

  if (isListOpen) {
    parts.push('</ol>');
  }

  return parts.join('');
}

function convertHtmlToBulletPointsIfNeeded(html: string): string {
  const normalizedHtml = convertEscapeSequencesToNewlines(html);
  const hasNewlines = normalizedHtml.includes('\n');
  const hasMultipleBlocks = hasMultipleBlockElements(html);

  if (!hasNewlines && !hasMultipleBlocks) {
    return returnHtmlUnlessEmpty(html);
  }

  if (hasNewlines && hasNewlinesBetweenElements(html)) {
    const lines = splitHtmlByNewlinesPreservingFormatting(html);
    const nonEmptyLines = lines.filter(isNotEmpty);

    return isEmpty(nonEmptyLines)
      ? returnHtmlUnlessEmpty(html)
      : buildBulletListFromHtmlLines(nonEmptyLines);
  }

  if (hasMultipleBlocks && hasFormattingInBlockElements(html)) {
    const blockContents = extractInnerHtmlFromBlockElements(html);

    if (isNotEmpty(blockContents)) {
      const parts = blockContents.map(
        (content) =>
          `<li data-list="bullet"><span class="ql-ui" contenteditable="false"></span>${content}</li>`,
      );

      return `<ol>${parts.join('')}</ol>`;
    }
  }

  const processedText = extractTextFromHtml(html);

  if (isEmpty(processedText)) {
    return '';
  }

  const normalizedText = convertEscapeSequencesToNewlines(processedText);

  if (normalizedText.includes('\n')) {
    return buildBulletPointsHtml(splitTextIntoNonEmptyLines(normalizedText));
  }

  return html;
}

function convertPlainTextToBulletPointsIfNeeded(text: string): string {
  const normalizedText = convertEscapeSequencesToNewlines(text);

  if (normalizedText.includes('\n')) {
    const lines = splitTextIntoNonEmptyLines(normalizedText);

    return buildBulletPointsHtml(lines);
  }

  return escapeHtml(text);
}

export function convertTextToBulletPoints(text?: string): string {
  if (isEmpty(text)) {
    return '';
  }

  if (isHtmlContent(text)) {
    const hasLists = LIST_PATTERN.test(text);

    if (hasLists) {
      const cleaned = cleanHtmlWithLists(text);

      return convertTextBeforeListToBulletPoints(cleaned);
    }

    return convertHtmlToBulletPointsIfNeeded(text);
  }

  return convertPlainTextToBulletPointsIfNeeded(text);
}
